// Quais sons tocam para os eventos de cada ação, seguindo as regras de mixagem:
// o seu turno soa cheio; turno de bot ou de outro jogador toca mais baixo e curto;
// "acelerar bots" deixa só conquista, eliminação, boss e raid.
import { type GameEvent, type LootKind, type PlayerId, type PlayerView } from '@estandarte/engine';
import { play, playDice, type SoundName } from './sound';

const LOOT_SOUND: Record<LootKind, SoundName> = {
  strike: 'runa',
  barrage: 'fogo',
  strength: 'pocao',
  precision: 'pocao',
  blessing: 'runa',
  wall: 'runa',
  restore: 'runa',
  ward: 'runa',
  paralyze: 'runa',
  luck: 'amuleto',
  captain: 'barco',
  teleport: 'runa',
  reinforce: 'moedas',
  draw: 'moedas',
  steal: 'moedas',
};

/** Sons que sobrevivem ao "acelerar bots". */
const BIG = new Set<SoundName>(['conquista', 'eliminacao', 'boss', 'raid']);

function actorOf(e: GameEvent): PlayerId | null {
  switch (e.type) {
    case 'combat':
    case 'combatPreview':
      return e.attacker;
    case 'setupPlaced':
    case 'placed':
    case 'trade':
    case 'conquest':
    case 'moveIn':
    case 'fortify':
    case 'cardDrawn':
    case 'lootDrawn':
    case 'lootChosen':
    case 'lootPlayed':
    case 'lootDiscarded':
    case 'power':
    case 'heal':
    case 'harmony':
    case 'bossCombat':
    case 'bossKilled':
    case 'reaction':
      return e.player;
    case 'eliminated':
      return e.by;
    default:
      return null;
  }
}

/**
 * Sequência de dados: a rolagem e, para cada comparação, aço (você vence) ou golpe (você perde).
 * Devolve quanto tempo ela ocupa, para o próximo som esperar.
 */
function diceSequence(att: number[], def: number[], youAttack: boolean, quiet: boolean, at: number, light = false): number {
  const settle = playDice({ quiet: quiet || light, delay: at });
  const pairs = Math.min(att.length, def.length);
  if (light) return settle + 0.35;
  const a = [...att].sort((x, y) => y - x);
  const d = [...def].sort((x, y) => y - x);
  for (let i = 0; i < pairs && i < 3; i++) {
    const attackerWins = a[i] > d[i];
    const mineWins = youAttack ? attackerWins : !attackerWins;
    play(mineWins ? 'aco' : 'impacto', { quiet, delay: at + settle + i * 0.32 });
  }
  return settle + pairs * 0.32 + 0.45;
}

/** Combates de uma mesma ação ("atacar até o fim") com som completo; os do meio ficam só com o lançamento. */
const FULL_COMBATS = 3;

/**
 * Toca os sons dos eventos de uma ação, um depois do outro, cada um com seu espaço
 * (nada de dez sons ao mesmo tempo). Devolve a duração total em segundos: o jogo
 * espera esse tempo antes da próxima jogada de bot.
 */
export function playEvents(
  events: GameEvent[],
  view: PlayerView,
  me: PlayerId | null,
  opts: { fastBots: boolean },
): number {
  let t = 0;
  const combats = events.filter((e) => e.type === 'combat').length;
  let combatIdx = 0;
  for (const e of events) {
    const actor = actorOf(e);
    const isBot = actor !== null && view.players[actor]?.kind === 'bot';
    const mine = actor !== null && actor === me;
    const quiet = actor !== null && !mine;
    const skipSmall = opts.fastBots && isBot;
    /** Toca e reserva `gap` segundos antes do próximo som (encurtado quando o som foi pulado). */
    const emit = (name: SoundName, gap: number, extra = 0) => {
      if (skipSmall && !BIG.has(name)) {
        t += gap * 0.25;
        return;
      }
      play(name, { quiet: quiet && !BIG.has(name), delay: t + extra });
      // "Acelerar bots": os sons grandes ainda tocam, com metade da folga.
      t += (gap + extra) * (skipSmall ? 0.5 : 1);
    };
    switch (e.type) {
      case 'setupPlaced':
      case 'placed':
        emit('peca', 0.3);
        break;
      case 'trade':
        emit('troca', 1);
        break;
      case 'combat': {
        combatIdx++;
        if (skipSmall) {
          t += 0.2;
          break;
        }
        if (e.sea || e.notes.includes('captain')) emit('barco', 0.5);
        const youAttack = me === null || e.attacker === me || e.defender !== me;
        // Num "atacar até o fim" longo, os primeiros e o último soam inteiros; os do meio, só o lançamento.
        const light = combats > FULL_COMBATS + 1 && combatIdx > FULL_COMBATS && combatIdx < combats;
        t += diceSequence(e.attackRolls, e.defenseRolls, youAttack, quiet, t, light);
        break;
      }
      case 'combatPreview':
        if (e.reactor === me) emit('amuleto', 0.6);
        break;
      case 'conquest':
        emit('conquista', 1.1, 0.15);
        break;
      case 'moveIn':
      case 'fortify':
        emit('marcha', 0.9);
        break;
      case 'eliminated':
        emit('eliminacao', 2.2, 0.3);
        break;
      case 'cardDrawn':
      case 'lootChosen':
      case 'bossDamageReward':
      case 'regionFirst':
        emit('carta', 0.45);
        break;
      case 'lootPlayed':
        emit(LOOT_SOUND[e.kind], 1);
        break;
      case 'harmony':
        emit('runa', 1);
        break;
      case 'turnStart':
        if (e.player === me && view.players[e.player]?.kind === 'human') play('turno', { delay: t + 0.2 });
        t += 0.6;
        break;
      case 'bossSpawn':
        play('boss', { delay: t });
        t += 2.6;
        break;
      case 'bossCombat':
        if (skipSmall) {
          t += 0.2;
          break;
        }
        {
          const settle = playDice({ quiet, delay: t });
          play(e.bossLoss > 0 ? 'bossDano' : 'impacto', { quiet, delay: t + settle });
          t += settle + 0.8;
        }
        break;
      case 'bossKilled':
        play('bossMorte', { delay: t + 0.3 });
        t += 2.8;
        break;
      case 'bossRaid':
        play(e.raid === 'freeze' ? 'gelo' : 'raid', { delay: t });
        if (e.raid === 'freeze') play('raid', { delay: t + 0.2, quiet: true });
        t += 2.7;
        break;
      case 'gameOver': {
        const won = me === null ? view.players[e.result.winner]?.kind === 'human' : e.result.winner === me;
        if (e.result.reason === 'serverSave') {
          play('serverSave', { delay: t });
          play(won ? 'vitoria' : 'derrota', { delay: t + 3.6 });
          t += 6;
        } else {
          play(won ? 'vitoria' : 'derrota', { delay: t + 0.8 });
          t += 3;
        }
        break;
      }
    }
  }
  return t;
}
